import { useRouteQuery } from '@/common/route/useRouteQuery';
import { pageSize } from '@/common/utils/helpers';
import { StringUtils } from '@/common/utils/stringUtils';
import { validateIpAddress } from '@/common/validator/validatorHelpers';
import { useBaseListView } from '@/common/view/useBaseListView';
import { useCrudActions } from '@/common/view/useCrudActions';
import { activityService } from '@/services/activity';
import { applicationService } from '@/services/applications';
import useVuelidate from '@vuelidate/core';
import { helpers, numeric, or, required } from '@vuelidate/validators';
import { computed, ref } from 'vue';
import { ActivityFilterType } from './mixins';

export function useAccountActivity() {
    const { query, updateQueryToRoute } = useRouteQuery({
        definition: {
            type: { type: 'number', default: ActivityFilterType.AccountId.value },
            criteria: { type: 'string', default: '' },
            offset: { type: 'number', default: 0 },
            limit: { type: 'number', default: pageSize }
        },
        onChange: () => fetchList()
    });

    const validations = {
        criteria: {
            required,
            validIpAddress: helpers.withMessage(
                `Invalid ip address`,
                or(() => query.type !== ActivityFilterType.IpAddress.value, validateIpAddress())
            ),
            validAccountId: helpers.withMessage(
                `Value must be numeric`,
                or(() => query.type !== ActivityFilterType.AccountId.value, numeric)
            )
        }
    };

    const v$ = useVuelidate(validations, query);

    const isBanned = ref(false);
    const accountId = computed(() => {
        if (query.type !== ActivityFilterType.AccountId.value || v$.value.$invalid) {
            return null;
        }

        return StringUtils.toNumber(query.criteria);
    });

    const checkAccountBanned = useCrudActions().executeAction({
        request: applicationService.account.isBanned,
        onSuccess: ({ data }) => (isBanned.value = data ?? false),
        onError: () => void (isBanned.value = false)
    });

    const { list, total, fetchList } = useBaseListView({
        request: activityService.account.list,
        paramsBuilder: () => ({ ...query }),
        hooks: {
            onPageChange: offset => (query.offset = offset),
            canFetch: () => {
                v$.value.$touch();
                return !v$.value.$invalid;
            },
            onSuccess: async () => {
                updateQueryToRoute();
                if (accountId.value) {
                    await checkAccountBanned(accountId.value);
                }
            }
        }
    });

    return { accountId, isBanned, query, v$, list, total, fetchList };
}
